import { ReferenceBrandType } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceBrandType';
import { ReferenceStock } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import { Season } from '@1po/1po-bff-fe-spec/generated/tire/model/Season';
import { TireItem } from '@1po/1po-bff-fe-spec/generated/tire/model/TireItem';
import { TFunction } from 'i18next';
import queryString, { ParsedQuery } from 'query-string';
import { TextFilterItem } from 'components/Filter/Filter.types';
import { getDiscountedPrice } from 'components/ReferencePriceSection';
import { SortData, TreeDataNode } from 'components/Sort/Sort.types';
import { asc, desc, multiSort } from 'components/Sort/Sort.utils';
import { MyStoreTireBrandLocal } from 'domains/myStore/MyStore.types';
import { NON_MKTP, ReferencePriceType } from 'domains/references/References.types';
import {
  sortReferencesByPrice,
  sortReferencesByStocks,
  sortTireReferencesByLifecycle,
  sortTireWithoutPriceLast,
} from 'domains/tires/Tire.compare';
import {
  ALL,
  AVAILABILITY_ASC,
  AVAILABILITY_DESC,
  PRICE_ASC,
  PRICE_DESC,
  SUMMER,
  SUMMER_WINTER,
  TireBrandsParam,
  TireFilterParamPrefix,
  TireQueryParam,
  TireReference,
  TireSeasonParam,
  TireSet,
  TireSetParam,
  TireSortParam,
  TireSortReference,
  TireUrlQuerySearchParams,
  WINTER,
} from 'domains/tires/Tire.types';
import { SparePartsViewType } from 'domains/user';
import { getTireDiameter } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/TireReferenceCardUtils';
import { getData, getSearchData, isNonEmptyString, NO_DATA, SearchData, toStringArray } from 'utils';

interface SearchParams {
  searchParamsBase64: string;
  tireSearchParams: string;
  width: string;
  series: string;
  diameter: string;
  chargeIndicator: string;
  doubleChargeIndicator?: string;
  speedIndicator: string;
}

interface TireSearchQuery {
  chargeIndicator?: string;
  doubleChargeIndicator?: string;
}

export function getQueryWithDoubleChargeIndicator(
  regExp: RegExpMatchArray | undefined,
  separator?: string,
): TireSearchQuery {
  if (!regExp || regExp.index === undefined || !separator) {
    return {};
  }
  const availableLoadIndexes = regExp[0]?.split(separator) ?? [];
  if (availableLoadIndexes.length <= 1 || availableLoadIndexes[0].startsWith(availableLoadIndexes[1])) {
    return { chargeIndicator: availableLoadIndexes[0] };
  }
  const [first, second] = availableLoadIndexes;
  const [lower, higher] = parseInt(first, 10) < parseInt(second, 10) ? [first, second] : [second, first];

  return { chargeIndicator: lower, doubleChargeIndicator: higher };
}

export const toSearchParams = (
  query: string,
  season: string | undefined,
  brands: string[],
  chargeIndicatorSeparator?: string,
): SearchParams => {
  const MAX_LEN_WHEN_DOUBLE_LOAD = 6;
  const MAX_LEN_WHEN_NODOUBLE_LOAD = 5;
  const params = query.split(' ');
  const width = params[0] ?? '';
  const series = params[1] ?? '';
  const diameter = params[2] ?? '';
  const chargeIndicator = params.length >= 4 ? params[3] : '';
  const speedIndicator = (() => {
    switch (params.length) {
      case MAX_LEN_WHEN_NODOUBLE_LOAD:
        return params[MAX_LEN_WHEN_NODOUBLE_LOAD - 1];
      case MAX_LEN_WHEN_DOUBLE_LOAD:
        return params[MAX_LEN_WHEN_DOUBLE_LOAD - 1];
      default:
        return '';
    }
  })();

  const { chargeIndicator: newChargeIndicator, doubleChargeIndicator } = getQueryWithDoubleChargeIndicator(
    RegExp(String.raw`\d+${chargeIndicatorSeparator}\d+`).exec(query) ?? undefined,
    chargeIndicatorSeparator,
  );

  const allBrands = brands.join('-');
  const rawInitialChargeIndicator = chargeIndicator ? `-${chargeIndicator}` : '';
  const rawNewChargeIndicator = newChargeIndicator ? `-${newChargeIndicator}` : '';
  const rawChargeIndicator = rawNewChargeIndicator.length > 0 ? rawNewChargeIndicator : rawInitialChargeIndicator;
  const rawDoubleChargeIndicator = doubleChargeIndicator ? `-${doubleChargeIndicator}` : '';
  const rawSpeedIndicator = speedIndicator ? `-${speedIndicator}` : '';

  const chargeIndicatorOrEmpty = `${rawChargeIndicator}${rawDoubleChargeIndicator}` || '-';
  const tireSearchParams = `${width}-${series}-${diameter}${chargeIndicatorOrEmpty}${rawSpeedIndicator}`.toUpperCase();
  const allRequestParams = `${allBrands}-${season}-${tireSearchParams}`;

  return {
    searchParamsBase64: Buffer.from(allRequestParams).toString('base64'),
    tireSearchParams,
    width,
    series,
    diameter,
    chargeIndicator: newChargeIndicator && newChargeIndicator.length > 0 ? newChargeIndicator : chargeIndicator,
    doubleChargeIndicator,
    speedIndicator,
  };
};

export function convertDHTireTechnicalDesignationToSearchQuery(
  technicalDesignation: string | undefined,
): string | undefined {
  if (!technicalDesignation) {
    return undefined;
  }
  return technicalDesignation.replace('/ ', '').replace('R ', 'R').replace('Z ', 'Z');
}

export function convertDHTireSizeToSearchQuery(technicalDesignation: string | undefined): string | undefined {
  if (!technicalDesignation) {
    return undefined;
  }
  return technicalDesignation
    .split(',')?.[0]
    ?.replace('/', ' ')
    .replace(')', ' ')
    .replace('(', '')
    .replace('CP', '')
    .replace('C', '');
}

export function convertIAMTireSizeToSearchQuery(tireSize: string | undefined): string | undefined {
  if (!tireSize) {
    return undefined;
  }

  const lastChar = tireSize.at(-1) ?? '';
  if (lastChar?.match(/[a-zA-Z]/)) {
    tireSize = tireSize.replace(lastChar, ` ${lastChar}`);
  }

  return tireSize.replace('/', ' ').replace('R', ' R').replace('Z', ' Z');
}

export function mapDHTireUsageToSeason(usage: string | undefined): Season {
  switch (usage) {
    case 'Summer':
      return SUMMER;
    case 'Winter':
      return WINTER;
    case 'Summer and winter':
      return SUMMER_WINTER;
    default:
      return ALL;
  }
}

export function mapIAMTireUsageToSeason(usage: string | undefined): Season {
  switch (usage) {
    case 'Summer and winter':
      return SUMMER_WINTER;
    case 'winter only':
      return WINTER;
    case 'Summer':
      return SUMMER;
    default:
      return ALL;
  }
}

export const parseTireUrlQueryParams = (params: string): TireUrlQuerySearchParams | undefined => {
  try {
    const parsedParams: ParsedQuery<string | number> = queryString.parse(params, {
      arrayFormat: 'comma',
      types: {
        query: 'string',
        brands: 'string[]',
        season: 'string',
        tireSet: 'number',
        sort: 'string[]',
      },
    });

    return {
      query: isNonEmptyString(parsedParams[TireQueryParam]) ? (parsedParams[TireQueryParam] as string) : undefined,
      brands: toStringArray(parsedParams[TireBrandsParam]),
      season: isNonEmptyString(parsedParams[TireSeasonParam]) ? (parsedParams[TireSeasonParam] as Season) : ALL,
      tireSet:
        typeof parsedParams[TireSetParam] === 'number' && [2, 4, 6, 8].includes(parsedParams[TireSetParam])
          ? (parsedParams.tireSet as TireSet)
          : undefined,
      filters: transformFilterParams(parsedParams),
      sort: toStringArray(parsedParams[TireSortParam]),
    };
  } catch (error) {
    return undefined;
  }
};

const transformFilterParams = (params: ParsedQuery<string | number>): TextFilterItem[] => {
  return Object.entries(params)
    .filter(([key, value]) => {
      return key.startsWith(TireFilterParamPrefix) && value !== null && value !== undefined && value;
    })
    .flatMap(([key, value]) => {
      const id = key.replace(TireFilterParamPrefix, '');
      return toStringArray(value).map((item) => ({
        id,
        item,
      }));
    });
};

const getMyStoreTireBrandDiscount = (
  myStoreTiresDiscounts: Map<ReferenceBrandType, MyStoreTireBrandLocal> | undefined,
  brandName?: ReferenceBrandType,
  tireDiameter?: number,
) => {
  const tireRimSize = getTireDiameter(tireDiameter);
  if (brandName && tireRimSize) {
    const myStoreBrand = myStoreTiresDiscounts?.get(brandName);
    if (myStoreBrand) {
      return myStoreBrand.discounts.get(tireRimSize);
    }
  }
  return undefined;
};

export const mapTiresToArrayForSort = (
  tires: TireItem[],
  stockData: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>,
  prices: Map<string, NO_DATA | ReferencePriceType>,
  sparePartsView: SparePartsViewType,
  myStoreTiresDiscounts: Map<ReferenceBrandType, MyStoreTireBrandLocal> | undefined,
): TireSortReference[] => {
  return Array.from(tires, (tire) => {
    const stockEntry = stockData.get(tire.partNumber);
    const refStocks = stockEntry ? getSearchData(stockEntry)?.get(NON_MKTP) : undefined;

    const refPrices = getData(prices.get(tire.partNumber));
    const priceVatExcl = refPrices?.clientView?.recommendedPriceVatExcluded;

    const baseResult = {
      ...tire,
      sparePartsView,
      stocks: refStocks,
      price: refPrices,
    } as TireSortReference;

    if (baseResult.price?.clientView?.recommendedPriceVatExcluded) {
      const myStoreTireDiscount = getMyStoreTireBrandDiscount(myStoreTiresDiscounts, tire.brandName, tire.baseDiameter);
      return {
        ...baseResult,
        price: {
          ...baseResult.price,
          clientView: {
            ...baseResult.price.clientView,
            discountedRecommendedPriceVatExcluded: String(
              getDiscountedPrice(Number(priceVatExcl), myStoreTireDiscount?.discount),
            ),
          },
        },
      };
    }
    return baseResult;
  });
};

export const getSortedTires = (
  tires: TireItem[],
  stocksMap: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>,
  pricesMap: Map<string, NO_DATA | ReferencePriceType>,
  sparePartsView: SparePartsViewType,
  myStoreTiresDiscounts: Map<ReferenceBrandType, MyStoreTireBrandLocal> | undefined,
  sort?: SortData<TireSortReference>[],
): TireItem[] => {
  const mappedTiresForSort = mapTiresToArrayForSort(tires, stocksMap, pricesMap, sparePartsView, myStoreTiresDiscounts);
  const sorted = multiSort(mappedTiresForSort, !sort?.length ? [desc(sortReferencesByStocks)] : sort);
  return [...sorted]
    .sort(sortTireWithoutPriceLast)
    .sort(sortTireReferencesByLifecycle)
    .map((e) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { price, stocks, ...rest } = e;
      return rest;
    });
};

export const getTireSortTreeDataNodes = (t: TFunction): TreeDataNode<TireSortReference>[] => {
  const priceShortTitle = t('common.price.title', 'Price');
  const availabilityShortTitle = t('common.dialog.availability', 'Availability');
  const lowToHighLabel = t('common.sort.ascending_long', 'Low to High');
  const highToLowLabel = t('common.sort.descending_long', 'High to Low');
  return [
    {
      title: `${priceShortTitle}: ${lowToHighLabel}`,
      shortTitle: priceShortTitle,
      value: PRICE_ASC,
      key: PRICE_ASC,
      sectionPairNumber: 0,
      selectable: false,
      checkable: true,
      sortBy: asc(sortReferencesByPrice),
    },
    {
      title: `${priceShortTitle}: ${highToLowLabel}`,
      shortTitle: priceShortTitle,
      value: PRICE_DESC,
      key: PRICE_DESC,
      sectionPairNumber: 0,
      selectable: false,
      checkable: true,
      sortBy: desc(sortReferencesByPrice),
    },
    {
      title: `${availabilityShortTitle}: ${lowToHighLabel}`,
      shortTitle: availabilityShortTitle,
      value: AVAILABILITY_ASC,
      key: AVAILABILITY_ASC,
      sectionPairNumber: 1,
      selectable: false,
      checkable: true,
      sortBy: asc(sortReferencesByStocks),
    },
    {
      title: `${availabilityShortTitle}: ${highToLowLabel}`,
      shortTitle: availabilityShortTitle,
      value: AVAILABILITY_DESC,
      key: AVAILABILITY_DESC,
      sectionPairNumber: 1,
      selectable: false,
      checkable: true,
      sortBy: desc(sortReferencesByStocks),
    },
  ];
};

export const convertTireItemToTireReference = (tireItem: TireItem): TireReference => {
  return {
    brand: tireItem.brandName,
    name: tireItem.designation,
    referenceNumber: tireItem.partNumber,
  };
};
